import { createPath, generatePath, replace, type LoaderFunctionArgs } from 'react-router';

import { globalClient } from '~/query-client';
import { perms } from '~/routing/perms';
import { requirePerm } from '~/routing/requirePerm';

import { isPublisherBiddingPathname } from './supply-manager.utils';
import { supplyManagerPartnersQueryOptions } from './useSupplyManagerPartnersQuery';

export const INVENTORY_SUPPLY_MANAGER_PATH = '/:accountType/:accountId/inventory/supply-manager';
export const INVENTORY_PUBLISHER_BIDDING_PATH = `${INVENTORY_SUPPLY_MANAGER_PATH}/publisher-bidding`;

export const supplyManagerLoader = async (ctx: LoaderFunctionArgs) => {
  const url = new URL(ctx.request.url);
  if (isPublisherBiddingPathname(url.pathname)) {
    requirePerm(perms.publisherBiddingRouteAccess)(ctx);
  } else {
    requirePerm(perms.supplyManager)(ctx);
  }
  if (!ctx.params.accountId) {
    return null;
  }
  const campaign = url.searchParams.get('campaign');
  globalClient.prefetchQuery(
    supplyManagerPartnersQueryOptions({
      accountId: Number(ctx.params.accountId),
      enabled: true,
      productId: campaign && campaign !== 'all' ? campaign : undefined,
    }),
  );
  return null;
};

/**
 * Index route: when a non-all campaign is present, dual/bid-capable users are
 * replaced onto publisher-bidding. supply_only (and missing level) stay on the
 * index with the campaign param stripped so account-wide supply actions do not
 * inherit campaign-scoped query/mutation state.
 */
export const supplyManagerIndexLoader = async (ctx: LoaderFunctionArgs) => {
  const url = new URL(ctx.request.url);
  const campaign = url.searchParams.get('campaign');
  if (campaign && campaign !== 'all') {
    const sessionPlData = requirePerm(perms.supplyManager)(ctx);
    const inventoryAccessLevel =
      sessionPlData?.accountType === 'client'
        ? sessionPlData.permissions.inventoryAccessLevel
        : undefined;

    if (inventoryAccessLevel && inventoryAccessLevel !== 'supply_only') {
      requirePerm(perms.publisherBiddingRouteAccess)(ctx);
      throw replace(
        createPath({
          pathname: generatePath(INVENTORY_PUBLISHER_BIDDING_PATH, {
            ...ctx.params,
            accountId: sessionPlData.id.toString(),
            accountType: sessionPlData.accountType,
          }),
          search: new URLSearchParams(url.search).toString(),
        }),
      );
    }

    const searchParams = new URLSearchParams(url.search);
    searchParams.delete('campaign');
    throw replace(
      createPath({
        pathname: url.pathname,
        search: searchParams.toString(),
      }),
    );
  }
  return supplyManagerLoader(ctx);
};
